import { useEffect, useState, type JSX } from 'react';
import type { XeroStatus } from '@cca/xero-shared';
import {
  SettingsDrawer,
  ConfirmAction,
  Collapsible,
  PasskeysCard,
  accountSection,
  integrationSection,
  securitySection,
  type AdminSection,
  type AuditSession,
  type ViewerTier,
} from '@cca/ui';
import { api } from '../api/client';

/**
 * Finance Portal Settings — the estate-standard role-aware drawer. Replaces the
 * old "Dashboard" tab (which was really just Xero connection admin). Account for
 * everyone; the Xero integration and the registrar login audit for the registrar.
 */
export function FinanceSettings({
  open,
  onClose,
  viewer,
  xeroStatus,
  xeroError,
  onXeroChanged,
  onSignOut,
  onSignOutAll,
}: {
  open: boolean;
  onClose: () => void;
  viewer: ViewerTier;
  xeroStatus: XeroStatus | null;
  xeroError: string | null;
  onXeroChanged: () => void;
  onSignOut: () => void;
  onSignOutAll: () => void | Promise<void>;
}): JSX.Element {
  const [sessions, setSessions] = useState<AuditSession[] | null>(null);
  const [sessionsError, setSessionsError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!open || viewer !== 'superuser') return;
    let alive = true;
    api
      .loginHistory()
      .then(({ sessions }) => alive && setSessions(sessions))
      .catch(() => alive && setSessionsError('Could not load sign-ins.'));
    return () => {
      alive = false;
    };
  }, [open, viewer]);

  const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';
  const connected = xeroStatus?.connected === true;
  const xeroActions = connected ? (
    <ConfirmAction
      label="Disconnect Xero"
      prompt="Reconnecting needs the Xero OAuth flow."
      confirmLabel="Disconnect"
      onConfirm={async () => {
        await api.xeroDisconnect();
        onXeroChanged();
      }}
    />
  ) : (
    <a className="btn btn-primary btn-sm" href={`${apiBase}/api/xero/connect`}>
      Connect Xero
    </a>
  );

  const sections: AdminSection[] = [
    accountSection({
      identity: 'Signed in to the Finance Portal.',
      onSignOutThisDevice: onSignOut,
      onSignOutAll,
    }),
    integrationSection({
      name: 'Xero',
      connected,
      statusLine: connected
        ? `Connected · ${xeroStatus?.tenantName ?? 'unknown tenant'}`
        : 'Not connected',
      actions: xeroActions,
      minRole: 'superuser',
      ...(xeroError ? { error: xeroError } : {}),
    }),
    securitySection({
      title: 'Security',
      // Any signed-in user manages their OWN passkey (bead cca-wst); the login
      // audit below stays registrar-only (SecuritySection RoleGates it, and the
      // fetch above is superuser-only), so the section is safe to show to all.
      passkeys: (
        <Collapsible title="Passkeys — sign in without a code">
          <PasskeysCard
            apiBase={`${apiBase}/api/auth/passkeys`}
            authHeaders={() => {
              const t = localStorage.getItem('xero_session_token');
              return t ? { 'X-Session-Token': t } : {};
            }}
            loginHref="/"
            hideTitle
          />
        </Collapsible>
      ),
      sessions,
      ...(sessionsError ? { sessionsError } : {}),
    }),
  ];

  return <SettingsDrawer open={open} onClose={onClose} viewer={viewer} sections={sections} />;
}
